import { useRef, useState } from 'react';
import { UploadCloud, X } from 'lucide-react';
import { Button, Label, Text } from '@musa.tech/design-system';
import {
  ATTACHMENT_CONTENT_TYPES,
  MAX_ATTACHMENT_SIZE_BYTES,
  type AttachmentContentType,
} from '@/integrations/contestacao/client';

export const ACCEPTED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png';

/** Mesma validação nos dois pontos de anexo — abertura e complemento (RFC 6.4 e 6.5). */
export function validateEvidence(file: File): string | null {
  if (!ATTACHMENT_CONTENT_TYPES.includes(file.type as AttachmentContentType)) {
    return 'Formato não aceito. Envie PDF, JPG ou PNG.';
  }
  if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
    return 'Arquivo maior que 10MB.';
  }
  return null;
}

export function formatFileSize(bytes: number): string {
  const mb = bytes / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;
}

interface EvidencePickerProps {
  /** Precisa ser único na página: o histórico monta um formulário por contestação. */
  id: string;
  label: React.ReactNode;
  file: File | null;
  error?: string | null;
  disabled?: boolean;
  /**
   * Recebe o arquivo e a mensagem de recusa. Vem sempre o par porque quem decide se o formulário
   * pode ser enviado é o pai — validar aqui e guardar o erro só aqui deixaria o botão habilitado
   * com um arquivo que o picker já rejeitou.
   */
  onSelect: (file: File | null, error: string | null) => void;
}

/**
 * Seletor de evidência — usado na abertura da contestação e na resposta a uma solicitação de
 * informações. Estava inline no formulário de abertura; virou componente quando o complemento
 * passou a aceitar anexo (RFC 6.5: "texto **e/ou documentos** complementares"), para a validação
 * e o desenho não divergirem entre os dois lugares.
 *
 * O Design System 1.2.4 não tem componente de upload/dropzone — conferido em `dist/index.d.ts`.
 * A área é montada com primitivas do DS e tokens do preset, seguindo o desenho do protótipo
 * (borda tracejada, ícone de nuvem, rótulo + formatos). É um `<button>` de verdade, então recebe
 * foco e responde a Enter/Espaço; o drop é conveniência adicional, não o único caminho.
 */
export function EvidencePicker({
  id,
  label,
  file,
  error,
  disabled,
  onSelect,
}: EvidencePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = (candidate: File | null) => {
    if (!candidate) {
      onSelect(null, null);
      return;
    }
    const validationError = validateEvidence(candidate);
    onSelect(validationError ? null : candidate, validationError);
  };

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>
        <Text variant="label" size="small" className="text-neutral-700">
          {label}
        </Text>
      </Label>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={ACCEPTED_EXTENSIONS}
        className="sr-only"
        disabled={disabled}
        onChange={(event) => handleFile(event.target.files?.[0] ?? null)}
      />
      {file ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2">
          <Text variant="body" size="small" className="min-w-0 truncate text-neutral-800">
            {file.name} · {formatFileSize(file.size)}
          </Text>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => handleFile(null)}
            aria-label="Remover evidência"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          onDragOver={(event) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setIsDragging(false);
            handleFile(event.dataTransfer.files?.[0] ?? null);
          }}
          className={`flex w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed px-4 py-6 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
            isDragging
              ? 'border-primary-500 bg-primary-50'
              : 'border-neutral-300 bg-white hover:border-neutral-400 hover:bg-neutral-50'
          }`}
        >
          <UploadCloud className="h-6 w-6 text-neutral-400" />
          <Text variant="label" size="small" className="text-neutral-900">
            Anexar prova
          </Text>
          <Text variant="caption" size="xsmall" className="text-neutral-500">
            PDF, JPG ou PNG até 10MB
          </Text>
        </button>
      )}
      {error && (
        <Text variant="body" size="small" className="text-error-600">
          {error}
        </Text>
      )}
    </div>
  );
}
